import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useOrganisation } from "@/api/organisation-api";
import {
  useMyApplication,
  useSaveApplication,
  type ApplicationPatch,
  type CandidateApplication,
} from "@/api/application-api";

/**
 * The IMG candidate application. Step one of a sitting.
 *
 * STATIC BY DESIGN. The questions are fixed here in code, not assembled
 * from a pool — this is a recruitment methodology rather than a
 * questionnaire somebody edits weekly, and two things need to READ the
 * answers rather than merely display them: the panel console's stage 07
 * ("transfer from the candidate's submitted form"), and anyone asking a
 * question across candidates. Both want columns.
 *
 * The dynamic question engine it replaces still exists and still works.
 * It is not wired to anything. See CLAUDE.md, "Inert code kept on
 * purpose", for the file list and how to bring it back.
 *
 * STYLING IS THE REFERENCE'S, EXACTLY, via the `sk-*` classes in
 * styles/components.css — 16.8px inputs, 12px letter-spaced labels, the
 * 3px focus wash, the amber notice with its 4px left rule. Do not
 * rebuild a field out of Tailwind utilities here; the classes exist so
 * the numbers live in one place.
 *
 * The repeated rows — confidence, throughput, procedures — are driven by
 * the three consts below, mirroring the reference's own CONF / VOLS /
 * PROCS arrays. Their ids are load-bearing: they are the keys the answer
 * is stored under AND the ids the panel template uses, which is what
 * makes the transfer a lookup.
 */

export const CONF: { id: string; label: string; hint?: string }[] = [
  { id: "dictaphone", label: "Dictaphone / DECT handset", hint: "Ward and reporting-room handsets" },
  { id: "vr", label: "Voice recognition reporting", hint: "Including editing your own text before sign-off" },
  { id: "templates", label: "Structured report templates / macros" },
  { id: "pacs", label: "PACS / RIS worklist navigation", hint: "Finding priors, flagging urgents" },
  { id: "emr", label: "Electronic medical record lookup" },
];

export const VOLS: { id: string; label: string }[] = [
  { id: "xr", label: "X-ray" },
  { id: "ct", label: "CT" },
  { id: "mri", label: "MRI" },
  { id: "us", label: "Ultrasound" },
  { id: "mammo", label: "Mammography / breast" },
  { id: "proc", label: "Fluoroscopy / procedures" },
];

const PROCS = [
  "US-guided biopsy",
  "US-guided drainage",
  "CT-guided biopsy",
  "Fluoroscopy (barium/contrast studies)",
  "Joint injection / arthrogram",
  "Central line / PICC insertion",
  "Angiography",
  "Nephrostomy / biliary drainage",
  "None of these",
];

/**
 * The one option in PROCS that contradicts every other.
 *
 * "None of these" alongside "US-guided biopsy" is not a stricter answer
 * or a looser one, it is an unreadable one -- and the reviewer scoring
 * the row cannot tell which half the candidate meant. So the two are
 * mutually exclusive, enforced in BOTH directions: the tick is disabled
 * while anything else is on, and ticking anything else clears it. One
 * direction alone still permits the contradiction, just via a different
 * click order.
 */
const PROC_NONE = "None of these";

/** Every field that must be answered before the form can be submitted. */
/**
 * Every field that must be answered before the form can be submitted.
 *
 * THIRTY, and the count is the reference's own (`data-req`), not a
 * judgement of mine. The four tick-boxes and the confidence grid are
 * counted separately below, for 35 in total.
 */
const REQUIRED: (keyof CandidateApplication)[] = [
  // 01
  "full_name",
  "email",
  "phone",
  "country_of_training",
  // 02
  "ahpra_status",
  "ranzcr_pathway",
  "psv_status",
  "english_requirement",
  "work_rights",
  // 03
  "primary_qualification",
  "years_since_qualification",
  "current_post",
  "last_independent_report",
  "unsupervised_experience",
  // 05
  "throughput_context",
  // 07
  "why_role",
  "first_12_months",
  "path_to_fellowship",
  "years_three_to_five",
  "beyond_five_years",
  "support_needed",
  "notice_period",
  "able_to_relocate",
  // 08
  "sj_retrospective_miss",
  "sj_pressured_report",
  "sj_junior_disagrees",
  "sj_long_shift",
  "sj_was_wrong",
  // 09
  "bond_stance",
  "bond_risk",
];

/** The four boxes that must be ticked. Counted with the fields above. */
const REQUIRED_TICKS: (keyof CandidateApplication)[] = [
  "bond_acknowledged",
  "declare_true",
  "declare_verification",
  "declare_live_assessment",
];

/**
 * The fifth tick, required ONLY on a repeat sitting.
 *
 * Kept out of REQUIRED_TICKS above because that list is a constant and
 * this one is conditional: a first application carries nothing, so
 * demanding the confirmation would be asking somebody to re-affirm
 * answers they have just typed. The server applies the same condition.
 */
const CARRIED_TICK: keyof CandidateApplication = "carried_confirmed";

/**
 * The return-of-service terms, as shown to the candidate.
 *
 * HARD-CODED, and that is a decision to revisit rather than a finished
 * one. These are the PRACTICE'S commercial terms, not the software's,
 * and the honest home for them is organisation settings alongside the
 * name and the logo. They are here because the reference states them as
 * fixed copy and because a half-configurable bond — a number in the
 * database and a table in the markup — is worse than either. Change
 * them here until they become settings, and change the schedule with
 * the years: the two must agree or the table contradicts the sentence
 * above it.
 */
const BOND = {
  years: 4,
  yearsWord: "four",
  cap: "AUD $200,000",
  schedule: [
    { when: "Year 1 (months 1–12)", proportion: "100%", amount: "$200,000" },
    { when: "Year 2 (months 13–24)", proportion: "75%", amount: "$150,000" },
    { when: "Year 3 (months 25–36)", proportion: "50%", amount: "$100,000" },
    { when: "Year 4 (months 37–47)", proportion: "25%", amount: "$50,000" },
    { when: "On completing 48 months", proportion: "Nil", amount: "$0" },
  ],
} as const;


/**
 * The option lists, keyed by field, because TWO things need them.
 *
 * ``select`` renders them, and ``filled`` checks membership: a stored
 * value that is not in its list renders as the blank "Select…" option
 * while a naive emptiness test still calls it answered. That happened —
 * a seeded value of "Employer-sponsored visa required" showed an empty
 * Work rights box that never raised an error, because the string was
 * non-empty. It would happen again the day an option is reworded, to
 * every candidate holding the old wording.
 */
const SELECT_OPTIONS: Partial<Record<keyof CandidateApplication, string[]>> = {
  ahpra_status: [
    "Specialist registration granted",
    "Limited registration (area of need)",
    "Provisional registration",
    "Application submitted — awaiting outcome",
    "Not yet applied",
  ],
  ranzcr_pathway: [
    "Assessed — substantially comparable",
    "Assessed — partially comparable",
    "Defined Scope pathway",
    "Expedited Specialist pathway",
    "Application in progress",
    "Not yet applied",
  ],
  psv_status: ["Complete", "In progress", "Not started"],
  english_requirement: [
    "IELTS — meets standard",
    "OET — meets standard",
    "PTE Academic — meets standard",
    "Exemption pathway",
    "Booked / not yet sat",
  ],
  work_rights: [
    "Citizen / permanent resident",
    "Visa granted",
    "Visa application lodged",
    "Would need sponsorship",
  ],
  able_to_relocate: ["Yes, without conditions", "Yes, with support", "Need to discuss"],
  bond_stance: [
    `I am willing to commit to ${BOND.yearsWord} years post-Fellowship on these terms`,
    "I am willing in principle, but want to discuss the detail first",
    "I would want to negotiate the length or the amount",
    "I am not comfortable with a return-of-service bond",
    "I need to take legal advice before answering",
  ],
};

const REQUIRED_SET = new Set<string>(REQUIRED as string[]);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Draft = Partial<CandidateApplication>;

/**
 * One labelled field.
 *
 * DEFINED AT MODULE LEVEL, not inside the form. A component declared in
 * a render body is a NEW component type on every render, so React
 * unmounts and remounts its subtree — which for a wrapper around an
 * <input> means the field loses focus after every single keystroke.
 * Same for Card below.
 */
function Field({
  name,
  label,
  required,
  help,
  children,
}: {
  name: string;
  label: string;
  required?: boolean;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="sk-field">
      <label className="sk-label" htmlFor={`f-${name}`}>
        {label}
        {required && <span className="sk-req">*</span>}
      </label>
      {children}
      {help && <p className="sk-help">{help}</p>}
    </div>
  );
}

function Card({
  code,
  title,
  hint,
  carried,
  children,
}: {
  code: string;
  title: string;
  hint?: string;
  /** When these answers were carried from a previous sitting. */
  carried?: string | null;
  children: React.ReactNode;
}) {
  return (
    <section className="sk-card" data-cy={`app-section-${code}`}>
      <div className="sk-card-head">
        <span className="sk-code">{code}</span>
        <h2 className="sk-card-title">{title}</h2>
        {/* MARKED, not silently prefilled. A carried answer looks
            identical to one typed a moment ago, and the panel scores
            stage 07 against these numbers -- so a year-old throughput
            figure presented as current is a claim nobody made today. */}
        {carried && (
          <span className="sk-carried" data-cy={`app-carried-${code}`}>
            From {carried} — check it is still correct
          </span>
        )}
      </div>
      <div className="sk-card-body">
        {hint && <p className="sk-hint">{hint}</p>}
        {children}
      </div>
    </section>
  );
}

/**
 * Disables the form for a reader, and does NOTHING AT ALL otherwise.
 *
 * THE CANDIDATE'S MARKUP MUST NOT MOVE. Making the form's own wrapper a
 * <fieldset> disabled the controls in one line, and changed the element
 * the candidate renders -- a fieldset carries its own default
 * min-width and box behaviour, so "it should look the same" was a claim
 * about CSS rather than a fact about the DOM. A candidate sitting an
 * exam is not the place to find out.
 *
 * So the fieldset only exists for the reviewer. For the candidate this
 * is a fragment: same tree, same classes, nothing added.
 */
function ReadOnlyGate({
  readOnly,
  children,
}: {
  readOnly: boolean;
  children: React.ReactNode;
}) {
  if (!readOnly) return <>{children}</>;
  return (
    <fieldset disabled className="m-0 min-w-0 border-0 p-0">
      {children}
    </fieldset>
  );
}

interface Props {
  onContinue?: () => void;
  /** The exam's own preamble, shown above the form. */
  prompt?: string | null;
  /**
   * Somebody ELSE'S submitted answers, rendered read-only.
   *
   * THE REVIEWER READS THE SAME COMPONENT THE CANDIDATE FILLED IN, and
   * that is the whole point of the prop. A second component rendering
   * the same nine sections is a second set of labels, options and help
   * lines to keep in step -- and this repo has already shipped a form
   * that "looked right and asked different questions" once. One
   * component cannot drift from itself.
   *
   * Given, the own-row query is switched OFF: it reads the signed-in
   * person's application and a reviewer calling it gets a 403.
   */
  answers?: CandidateApplication | null;
}

export function CandidateApplicationForm({
  onContinue,
  prompt,
  answers,
}: Props) {
  const readOnly = answers != null;
  const query = useMyApplication(!readOnly);
  const save = useSaveApplication();
  // The stored row, whoever it belongs to.
  const source = answers ?? query.data ?? null;
  // The practice's own name, not the reference's. It appears three times
  // in the bond terms and once in the declaration; a hard-coded name
  // there would be somebody else's letterhead on a financial commitment.
  const org = useOrganisation();
  const practice = org.data?.name?.trim() || "The practice";

  // Sections 01-06 were carried from a previous sitting, and this is
  // when they were last affirmed. Null on a first application.
  //
  // The DATE is the point. "Still correct?" is unanswerable without
  // knowing how old the answer is -- a candidate re-affirming a
  // throughput figure needs to know whether they gave it last month or
  // last year.
  const carriedFrom = source?.carried_from
    ? new Date(source.carried_from).toLocaleDateString(undefined, {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  const [draft, setDraft] = useState<Draft>({});
  const [submitted, setSubmitted] = useState(false);
  const seeded = useRef(false);

  /**
   * The authoritative draft, updated synchronously.
   *
   * `draft` state is for RENDERING. Handlers that build a new value out
   * of the old one — the confidence grid, the volumes, the procedure
   * checkboxes — must read this instead, because two changes in the same
   * tick both see the same stale `draft` and the second silently
   * discards the first. Rating five systems quickly should leave five
   * ratings, not one.
   */
  const draftRef = useRef<Draft>({});

  useEffect(() => {
    if (!seeded.current && source) {
      seeded.current = true;
      draftRef.current = source;
      setDraft(source);
    }
  }, [source]);

  const v = <K extends keyof CandidateApplication>(k: K) => draft[k];
  const str = (k: keyof CandidateApplication) => (v(k) as string | null) ?? "";
  const num = (k: keyof CandidateApplication) =>
    v(k) === null || v(k) === undefined ? "" : String(v(k));

  /**
   * Saving.
   *
   * `set` is for typing: the draft updates now and the change is queued,
   * then flushed 800ms after the candidate stops. `commit` is for
   * choices — a select, a scale button, a checkbox — where there is no
   * "still typing" and the save should go at once.
   *
   * DEBOUNCED RATHER THAN BLUR-ONLY. Saving on blur alone loses whatever
   * is in the field the candidate was in when they close the laptop, and
   * the last field is exactly the one somebody stops on. The form tells
   * them their answers save as they go; that has to be true.
   *
   * Only CHANGED fields are queued, so a flush can never resurrect a
   * stale copy of a section they have since edited elsewhere.
   */
  const pending = useRef<ApplicationPatch>({});
  const timer = useRef<number | null>(null);
  const mutate = save.mutate;

  const flush = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    const patch = pending.current;
    pending.current = {};
    if (Object.keys(patch).length > 0) mutate(patch);
  }, [mutate]);

  const apply = (patch: ApplicationPatch) => {
    draftRef.current = { ...draftRef.current, ...patch };
    setDraft(draftRef.current);
    pending.current = { ...pending.current, ...patch };
  };

  const set = (patch: ApplicationPatch) => {
    apply(patch);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(flush, 800);
  };

  const commit = (patch: ApplicationPatch) => {
    apply(patch);
    flush();
  };

  useEffect(() => {
    // Leaving the page, by any route: navigating away, closing the tab,
    // or a phone backgrounding the browser (which never fires
    // beforeunload, so visibilitychange is the one that matters on
    // mobile).
    const onHide = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("beforeunload", flush);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("beforeunload", flush);
      document.removeEventListener("visibilitychange", onHide);
      flush();
    };
  }, [flush]);

  const filled = (k: keyof CandidateApplication) => {
    const value = draft[k];
    if (value === null || value === undefined) return false;
    if (typeof value === "number") return true;
    const text = String(value).trim();
    if (text === "") return false;
    const options = SELECT_OPTIONS[k];
    return options ? options.includes(text) : true;
  };

  const confidenceComplete = CONF.every(
    (c) => (draft.confidence ?? {})[c.id] != null,
  );

  const outstanding = useMemo(
    () => REQUIRED.filter((k) => !filled(k)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft],
  );
  /** The bond acknowledgement and the three declaration consents --
   *  plus, on a repeat sitting only, the carried-sections confirmation. */
  const ticksNeeded = useMemo(
    () =>
      source?.carried_from
        ? [...REQUIRED_TICKS, CARRIED_TICK]
        : REQUIRED_TICKS,
    [source?.carried_from],
  );
  const unticked = useMemo(
    () => ticksNeeded.filter((k) => draft[k] !== true),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft, ticksNeeded],
  );
  const emailBad = filled("email") && !EMAIL_RE.test(str("email").trim());

  // 30 fields + 4 ticks + the confidence grid = 35, which is the
  // reference's own count. A REPEAT sitting adds the carried-sections
  // confirmation, so the denominator moves to 36 rather than the
  // progress meter reading 36/35.
  const totalRequired = REQUIRED.length + ticksNeeded.length + 1;
  const done =
    REQUIRED.length -
    outstanding.length +
    (ticksNeeded.length - unticked.length) +
    (confidenceComplete ? 1 : 0);
  const pct = Math.round((done / totalRequired) * 100);
  const ready =
    outstanding.length === 0 &&
    unticked.length === 0 &&
    confidenceComplete &&
    !emailBad;

  const alreadySubmitted = !!source?.submitted_at;

  const volTotal = VOLS.reduce(
    (sum, r) => sum + (Number((draft.volumes ?? {})[r.id]) || 0),
    0,
  );

  const focusField = (name: string) => {
    const el = document.getElementById(`f-${name}`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    (el as HTMLElement | null)?.focus?.();
  };

  const doSubmit = () => {
    if (!ready) {
      setSubmitted(true);
      const first =
        outstanding[0] ?? unticked[0] ?? (emailBad ? "email" : null);
      if (first) focusField(String(first));
      return;
    }
    // Send the whole draft, not just what is pending: this is the one
    // save that must be complete, and it is cheap.
    if (timer.current !== null) window.clearTimeout(timer.current);
    pending.current = {};
    save.mutate({ ...draftRef.current, submitted: true } as ApplicationPatch, {
      onSuccess: () => onContinue?.(),
    });
  };

  if (!readOnly && query.isLoading) {
    return <p className="sk-help">Loading your application…</p>;
  }
  if (readOnly && !source) {
    return <p className="sk-help">This candidate has not applied yet.</p>;
  }

  // -- helpers ---------------------------------------------------------

  /** Only ever fires for a field that is actually required.
   *
   *  Without the REQUIRED_SET check every blank optional field turns red
   *  on a failed submit — thirty-two fields shouting at somebody who is
   *  twenty-one answers short, most of them about questions they were
   *  free to skip. */
  const err = (k: keyof CandidateApplication) =>
    submitted && REQUIRED_SET.has(k as string) && !filled(k) ? "Required" : null;

  const text = (
    k: keyof CandidateApplication,
    placeholder?: string,
  ) => (
    <>
      <input
        id={`f-${k}`}
        data-cy={`app-${k}`}
        className={`sk-input ${err(k) ? "sk-invalid" : ""}`}
        value={str(k)}
        placeholder={placeholder}
        onChange={(e) => set({ [k]: e.target.value } as ApplicationPatch)}
        onBlur={(e) => commit({ [k]: e.target.value } as ApplicationPatch)}
      />
      {err(k) && <span className="sk-error">{err(k)}</span>}
    </>
  );

  const area = (k: keyof CandidateApplication, placeholder?: string) => (
    <>
      <textarea
        id={`f-${k}`}
        data-cy={`app-${k}`}
        className={`sk-input ${err(k) ? "sk-invalid" : ""}`}
        value={str(k)}
        placeholder={placeholder}
        onChange={(e) => set({ [k]: e.target.value } as ApplicationPatch)}
        onBlur={(e) => commit({ [k]: e.target.value } as ApplicationPatch)}
      />
      {err(k) && <span className="sk-error">{err(k)}</span>}
    </>
  );

  /**
   * A required tick-box.
   *
   * Its own helper because these four carry weight the rest of the form
   * does not: an acknowledgement of a financial commitment and three
   * consents. Each gets an id so the error summary can jump to it, and
   * each saves immediately — there is no "still typing" on a checkbox.
   */
  const tick = (k: keyof CandidateApplication, label: string) => {
    const bad = submitted && draft[k] !== true;
    return (
      <div>
        <label className={`sk-check ${bad ? "sk-check-missing" : ""}`}>
          <input
            id={`f-${k}`}
            type="checkbox"
            data-cy={`app-${k}`}
            checked={draft[k] === true}
            onChange={(e) => commit({ [k]: e.target.checked } as ApplicationPatch)}
          />
          <span>{label}</span>
        </label>
        {bad && (
          <span className="sk-error" style={{ marginLeft: 33 }}>
            Please confirm this
          </span>
        )}
      </div>
    );
  };

  const select = (k: keyof CandidateApplication, options?: string[]) => (
    <>
      <select
        id={`f-${k}`}
        data-cy={`app-${k}`}
        className={`sk-input ${err(k) ? "sk-invalid" : ""}`}
        value={str(k)}
        onChange={(e) => commit({ [k]: e.target.value } as ApplicationPatch)}
      >
        <option value="">Select…</option>
        {(options ?? SELECT_OPTIONS[k] ?? []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      {err(k) && <span className="sk-error">{err(k)}</span>}
    </>
  );

  const number = (
    k: keyof CandidateApplication,
    min = 0,
    max = 60,
  ) => (
    <>
      <input
        id={`f-${k}`}
        data-cy={`app-${k}`}
        type="number"
        min={min}
        max={max}
        className={`sk-input ${err(k) ? "sk-invalid" : ""}`}
        value={num(k)}
        onChange={(e) =>
          set({
            [k]: e.target.value === "" ? null : Number(e.target.value),
          } as ApplicationPatch)
        }
        onBlur={(e) =>
          commit({
            [k]: e.target.value === "" ? null : Number(e.target.value),
          } as ApplicationPatch)
        }
      />
      {err(k) && <span className="sk-error">{err(k)}</span>}
    </>
  );

  // -- the form --------------------------------------------------------

  return (
    <div className="mx-auto w-full max-w-[880px]" data-cy="candidate-application">
      <ReadOnlyGate readOnly={readOnly}>
      <header className="sk-mast">
        <p className="sk-eyebrow">{practice} &middot; Application</p>
        <h1 className="sk-mast-title">Practice profile &amp; self-declaration</h1>
        {/* The lede is the form's own, ALWAYS. It says what this is and
            how long it takes, which an exam-specific preamble does not —
            and letting `intro_prompt` replace it meant an admin typing
            "Tell us about yourself." wiped the only explanation the
            candidate gets. The exam's prompt is shown as well, below. */}
        <p className="sk-mast-lede">
          This takes about 15 minutes. It covers your registration, reporting
          experience, systems use and plans. Answer honestly — everything here
          is checked against your logbook, your references and a live reporting
          session, and an inflated answer is easier to spot than a modest one.
        </p>
        {prompt && prompt.trim() !== "" && (
          <p className="sk-mast-lede" data-cy="app-exam-prompt">
            {prompt}
          </p>
        )}
      </header>

      {!readOnly && alreadySubmitted && (
        <div className="sk-notice" style={{ marginBottom: 16 }} data-cy="app-already-submitted">
          <h3>Already submitted</h3>
          <p>
            You submitted this on{" "}
            {new Date(source!.submitted_at!).toLocaleDateString()}. You can
            still correct anything — a change is better than an email about a
            change.
          </p>
        </div>
      )}

      {/* The candidate's own instrument: how much is left to answer.
          A reviewer reading a submitted application has nothing left to
          answer, and "36 of 36" beside somebody else's form is noise. */}
      {!readOnly && (
      <div className="sk-progress" data-cy="app-progress">
        <div className="sk-ptrack">
          <div
            className="sk-pfill"
            style={{ width: `${pct}%` }}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Application progress"
          />
        </div>
        <div className="sk-pmeta">
          <span>
            {pct === 0 ? "Not started" : pct === 100 ? "Ready to submit" : `In progress — ${pct}%`}
          </span>
          <span data-cy="app-progress-count">
            {done} of {totalRequired} required
          </span>
        </div>
      </div>
      )}

      {/* 01 */}
      <Card code="01" title="About you" carried={carriedFrom}>
        <div className="sk-grid">
          <Field name="full_name" label="Full name" required>{text("full_name")}</Field>
          <Field name="email" label="Email" required>
            <input
              id="f-email"
              data-cy="app-email"
              type="email"
              className={`sk-input ${err("email") || (submitted && emailBad) ? "sk-invalid" : ""}`}
              value={str("email")}
              onChange={(e) => set({ email: e.target.value })}
              onBlur={(e) => commit({ email: e.target.value })}
            />
            {submitted && emailBad && !err("email") && (
              <span className="sk-error">Enter a valid email</span>
            )}
            {err("email") && <span className="sk-error">Required</span>}
          </Field>
          <Field name="phone" label="Phone (with country code)" required>
            {text("phone", "+61 …")}
          </Field>
          <Field name="country_of_training" label="Country of primary training" required>
            {text("country_of_training")}
          </Field>
        </div>
      </Card>

      {/* 02 */}
      <Card
        code="02"
        carried={carriedFrom}
        title="Registration & eligibility"
        hint="If something is in progress, choose that option and give the date you expect it. In-progress is fine — unclear is not."
      >
        <div className="sk-grid">
          <Field name="ahpra_status" label="AHPRA / MCNZ registration" required>
            {select("ahpra_status")}
          </Field>
          <Field name="ranzcr_pathway" label="RANZCR assessment pathway" required>
            {select("ranzcr_pathway")}
          </Field>
          <Field name="defined_scope_area" label="If Defined Scope — which area?">
            {/* NO "Select…" here, and that is the reference's choice, not
                an omission: this is the one optional select whose first
                option is already the right answer for most people.
                Untouched it reads "Not applicable", which is what it
                means; the stored value stays null until they pick
                something, and null and "Not applicable" say the same
                thing to a reviewer. */}
            <select
              id="f-defined_scope_area"
              data-cy="app-defined_scope_area"
              className="sk-input"
              value={str("defined_scope_area") || "Not applicable"}
              onChange={(e) => commit({ defined_scope_area: e.target.value })}
            >
              {[
                "Not applicable",
                "Interventional radiology",
                "Interventional neuroradiology",
                "Paediatric radiology",
              ].map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </Field>
          <Field name="psv_status" label="Primary source verification (AMC/EPIC)" required>
            {select("psv_status")}
          </Field>
          <Field name="english_requirement" label="English requirement" required>
            {select("english_requirement")}
          </Field>
          <Field name="english_scores" label="Test scores (per component)">
            {text("english_scores", "e.g. L8.5 R8 W7.5 S8")}
          </Field>
          <Field name="work_rights" label="Work rights" required>
            {select("work_rights")}
          </Field>
          <Field
            name="registration_conditions"
            label="Any conditions on your registration?"
          >
            {text("registration_conditions", "e.g. 6 months supervised practice")}
          </Field>
        </div>
      </Card>

      {/* 03 */}
      <Card code="03" title="Qualifications &amp; experience" carried={carriedFrom}>
        <div className="sk-grid">
          <Field
            name="primary_qualification"
            label="Radiology qualification & year"
            required
          >
            {text("primary_qualification", "e.g. MD Radiodiagnosis, 2019")}
          </Field>
          <Field
            name="years_since_qualification"
            label="Years reporting since qualifying"
            required
          >
            {number("years_since_qualification", 0, 50)}
          </Field>
          <Field name="current_post" label="Current role & employer" required>
            {text("current_post")}
          </Field>
          <Field
            name="last_independent_report"
            label="Last date you reported independently"
            required
          >
            <input
              id="f-last_independent_report"
              data-cy="app-last_independent_report"
              type="date"
              className={`sk-input ${err("last_independent_report") ? "sk-invalid" : ""}`}
              value={str("last_independent_report")}
              onChange={(e) => commit({ last_independent_report: e.target.value })}
            />
            {err("last_independent_report") && (
              <span className="sk-error">Required</span>
            )}
          </Field>
          <Field
            name="subspecialty_interests"
            label="Subspecialty interests / fellowships"
          >
            {text("subspecialty_interests", "e.g. body MRI, neuro, breast")}
          </Field>
          <div className="sk-field sk-full">
            <label className="sk-label" htmlFor="f-unsupervised_experience">
              Have you ever worked unsupervised as the sole reporting
              radiologist? Describe.<span className="sk-req">*</span>
            </label>
            {area(
              "unsupervised_experience",
              "Where, for how long, and what was the on-call arrangement?",
            )}
          </div>
        </div>
      </Card>

      {/* 04 */}
      <Card
        code="04"
        carried={carriedFrom}
        title="Systems & dictation"
        hint="Rate your own day-to-day confidence. There is no penalty for a low score — we train for gaps, but we can't train for surprises on day one."
      >
        <p className="sk-scale-key">
          1 never used · 2 seen it · 3 with help · 4 independent · 5 fully fluent, could teach it
        </p>
        <div data-cy="app-confidence">
          {CONF.map((c) => (
            <div className="sk-row" key={c.id}>
              <div className="sk-row-label">
                {c.label}
                {c.hint && <small>{c.hint}</small>}
              </div>
              <div role="group" aria-label={c.label} style={{ display: "flex", gap: 4 }}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const on = (draft.confidence ?? {})[c.id] === n;
                  return (
                    <button
                      key={n}
                      type="button"
                      className="sk-scale-btn"
                      aria-pressed={on}
                      aria-label={`${c.label}: ${n}`}
                      data-cy={`app-conf-${c.id}-${n}`}
                      onClick={() => {
                        const next = { ...(draftRef.current.confidence ?? {}) };
                        // Pressing the chosen value again clears it, so a
                        // mis-click can be undone rather than only
                        // changed to another wrong answer.
                        if (next[c.id] === n) delete next[c.id];
                        else next[c.id] = n;
                        commit({ confidence: next });
                      }}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {submitted && !confidenceComplete && (
          <span className="sk-error">Rate every row</span>
        )}
        <div className="sk-grid" style={{ marginTop: 16 }}>
          <Field name="vr_system" label="Which voice recognition system?">
            {text("vr_system", "e.g. PowerScribe, Nuance, none")}
          </Field>
          <Field name="pacs_ris" label="Which PACS / RIS?">
            {text("pacs_ris", "e.g. Sectra, Agfa, Karisma")}
          </Field>
          <div className="sk-field sk-full">
            <label className="sk-label" htmlFor="f-reporting_method">
              If you have not used voice recognition, how do you currently produce reports?
            </label>
            {text("reporting_method", "e.g. typed directly, audio to typist, templates")}
          </div>
        </div>
      </Card>

      {/* 05 */}
      <Card
        code="05"
        carried={carriedFrom}
        title="Reporting capacity by modality"
        hint="On a normal working day, how many studies can you report to full sign-off standard? Give your sustainable number, not your best-ever day. Enter 0 for modalities you don't report."
      >
        <div data-cy="app-volumes">
          {VOLS.map((r) => (
            <div className="sk-row" key={r.id}>
              <div className="sk-row-label">{r.label}</div>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="number"
                  min={0}
                  max={400}
                  className="sk-vol"
                  aria-label={`${r.label}, cases per day`}
                  data-cy={`app-vol-${r.id}`}
                  value={(draft.volumes ?? {})[r.id] ?? ""}
                  onChange={(e) => {
                    const next = { ...(draftRef.current.volumes ?? {}) };
                    // Empty stays EMPTY rather than becoming 0: "I do not
                    // report this" and "I report none today" are different
                    // answers, and a total that counts blanks as zero is a
                    // number nobody can defend in an interview.
                    if (e.target.value === "") delete next[r.id];
                    else next[r.id] = Number(e.target.value);
                    set({ volumes: next });
                  }}
                  onBlur={() => commit({ volumes: draftRef.current.volumes ?? {} })}
                />
                <span className="sk-unit">/day</span>
              </span>
            </div>
          ))}
        </div>
        <div className="sk-total">
          <span className="sk-total-label">Your stated total</span>
          <span className="sk-total-value" data-cy="app-vol-total">
            {volTotal} <em>cases/day</em>
          </span>
        </div>
        <div className="sk-field sk-full" style={{ marginTop: 16 }}>
          <label className="sk-label" htmlFor="f-throughput_context">
            What counted as a &ldquo;case&rdquo; in your setting?
            <span className="sk-req">*</span>
          </label>
          {area(
            "throughput_context",
            "Were reports templated? Was a trainee or typist involved? Who signed off? What were the turnaround expectations?",
          )}
          <p className="sk-help">This is how we read your numbers fairly.</p>
        </div>
      </Card>

      {/* 06 */}
      <Card
        code="06"
        carried={carriedFrom}
        title="Procedures"
        hint="Tick only what you have performed independently in the last 12 months."
      >
        <div className="sk-checks" data-cy="app-procedures">
          {PROCS.map((p) => {
            const picked = draft.procedures ?? [];
            const on = picked.includes(p);
            const isNone = p === PROC_NONE;
            // "None of these" is unavailable while a real procedure is
            // ticked -- there is nothing it could mean.
            const blocked =
              isNone && picked.some((x) => x !== PROC_NONE);
            return (
              <label
                className="sk-check"
                key={p}
                style={blocked ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
                title={
                  blocked
                    ? "Untick the procedures above to choose this."
                    : undefined
                }
              >
                <input
                  type="checkbox"
                  checked={on}
                  disabled={blocked}
                  data-cy={`app-proc-${PROCS.indexOf(p)}`}
                  onChange={() => {
                    const cur = draftRef.current.procedures ?? [];
                    if (on) {
                      commit({ procedures: cur.filter((x) => x !== p) });
                      return;
                    }
                    // Ticking a real procedure clears "None of these" --
                    // the other half of the exclusion. Without it a
                    // candidate who ticks None first can still add
                    // procedures on top of it.
                    const base = isNone
                      ? []
                      : cur.filter((x) => x !== PROC_NONE);
                    commit({ procedures: [...base, p] });
                  }}
                />
                <span>{p}</span>
              </label>
            );
          })}
        </div>
        <div className="sk-field sk-full" style={{ marginTop: 16 }}>
          <label className="sk-label" htmlFor="f-procedure_log">
            Approximate numbers &amp; most recent date
          </label>
          {text("procedure_log", "e.g. ~40 US-guided biopsies, last Aug 2026")}
        </div>
      </Card>

      {/* 07 */}
      <Card code="07" title="Your plan">
        <div className="sk-grid">
          {(
            [
              {
                k: "why_role",
                label: "Why this role, this department, this location?",
                placeholder: "Be specific — what about this position in particular?",
              },
              {
                k: "first_12_months",
                subhead: "Your five-year plan — year by year",
                label: "First 12 months",
                help: "What do you want to have achieved by the end of your first year here?",
              },
              {
                k: "path_to_fellowship",
                label: "Path to RANZCR Fellowship",
                help: "Which exams or assessments remain, when do you intend to sit them, and what is your realistic completion date?",
              },
              {
                k: "years_three_to_five",
                label: "Years three to five",
                help: "Subspecialty direction, scope, teaching or research interests, and where you expect to be working.",
              },
              {
                k: "beyond_five_years",
                label: "Beyond five years",
                help: "Do you see yourself staying in this region long term? An honest answer is more useful to us than the answer you think we want.",
              },
              {
                k: "support_needed",
                subhead: "Practicalities",
                label: "What support would you need in your first 3 months?",
                placeholder:
                  "Orientation, supervision, systems training, subspecialty ramp-up.",
              },
            ] as const
          ).map((q) => (
            <Fragment key={q.k}>
            {"subhead" in q && q.subhead && (
              <div className="sk-subhead sk-full">{q.subhead}</div>
            )}
            <div className="sk-field sk-full">
              <label className="sk-label" htmlFor={`f-${q.k}`}>
                {q.label}
                <span className="sk-req">*</span>
              </label>
              {"help" in q && q.help && <p className="sk-help">{q.help}</p>}
              {area(q.k, "placeholder" in q ? q.placeholder : undefined)}
            </div>
            </Fragment>
          ))}
          <Field name="notice_period" label="Notice period / earliest start" required>
            {text("notice_period")}
          </Field>
          <Field
            name="able_to_relocate"
            label="Able to relocate to the role's location?"
            required
          >
            {select("able_to_relocate")}
          </Field>
        </div>
      </Card>

      {/* 08 */}
      <Card
        code="08"
        title="Situational judgement"
        hint="Five short scenarios. There is no single right answer and we are not testing recall — we want to see how you think, what you do first, and who you involve. A few sentences each is plenty. Answer as yourself, not as a textbook."
      >
        {[
          {
            k: "sj_retrospective_miss" as const,
            label: "1 · The retrospective miss",
            body: "You reported a staging CT as normal. Three weeks later the patient re-presents and the disease is clearly visible on your original study in retrospect. The registrar who spotted it has already told the treating team. What do you do, and in what order?",
          },
          {
            k: "sj_pressured_report" as const,
            label: "2 · The pressured report",
            body: "A senior referrer phones you, frustrated, and asks you to reword your report so it better supports the procedure they have already booked for tomorrow. They imply your wording is unhelpful and that the previous radiologist never made this difficult. How do you handle the call?",
          },
          {
            k: "sj_junior_disagrees" as const,
            label: "3 · The junior who disagrees",
            body: "A radiographer with two years' experience approaches you and says they think you have missed something on a study you signed off an hour ago. They seem nervous raising it. What happens next?",
          },
          {
            k: "sj_long_shift" as const,
            label: "4 · The end of a long shift",
            body: "It is the end of a heavy shift. You are tired, forty studies remain, and ED is calling every ten minutes about delays. Your next case is a complex MRI that deserves proper attention. What do you actually do?",
          },
          {
            k: "sj_was_wrong" as const,
            label: "5 · A time you were wrong",
            body: "Describe a real occasion when you were confident about a finding and turned out to be wrong. What happened, how did you find out, and what changed in your practice afterwards?",
          },
        ].map((s) => (
          <div className="sk-field" key={s.k}>
            <label className="sk-label" htmlFor={`f-${s.k}`}>
              {s.label}
              <span className="sk-req">*</span>
            </label>
            <p className="sk-help">{s.body}</p>
            {area(s.k)}
          </div>
        ))}
      </Card>

      {/* 09 */}
      <Card code="09" title="Return-of-service commitment">
        {/* WHAT WE ARE ASKING FOR.
            The section leads with the terms, not with the question. A
            candidate cannot answer "how do you feel about this
            commitment?" without first being shown the commitment, the
            cap and the pro-rata table — and asking them to agree to
            something they have not been shown is the same mistake as
            taking the de-identification tick before any image exists. */}
        <div className="sk-notice" data-cy="app-bond-terms">
          <h3>What we are asking for</h3>
          <p>
            {practice} invests substantially in an IMG appointment —
            recruitment, relocation, supervision, systems training and exam
            support. In return we ask for a{" "}
            <strong>
              {BOND.years}-year commitment beginning the day you complete your
              RANZCR Fellowship examinations
            </strong>
            .
          </p>
          <p>
            The employment contract will include a return-of-service clause. If
            you leave before the {BOND.years} years are complete, a proportion
            of {practice}&rsquo;s documented investment becomes repayable,{" "}
            <strong>capped at {BOND.cap}</strong> and{" "}
            <strong>reducing for each month served</strong>:
          </p>
          <table className="sk-bond">
            <thead>
              <tr>
                <th>If you leave during</th>
                <th>Proportion repayable</th>
                <th>Indicative amount</th>
              </tr>
            </thead>
            <tbody>
              {BOND.schedule.map((row) => (
                <tr key={row.when}>
                  <td>{row.when}</td>
                  <td>{row.proportion}</td>
                  <td>{row.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="sk-fine">
            Indicative only. Nothing on this form is a contract. The binding
            terms, the itemised schedule of {practice}&rsquo;s actual costs and
            the final pro-rata table will be provided in writing before you are
            asked to sign anything. We strongly encourage you to obtain
            independent legal advice and will allow reasonable time for it.
            Terms may be varied by negotiation, and hardship provisions will
            apply.
          </p>
        </div>

        <div className="sk-grid" style={{ marginTop: 16 }}>
          <div className="sk-field sk-full">
            <label className="sk-label" htmlFor="f-bond_stance">
              How do you feel about this commitment?
              <span className="sk-req">*</span>
            </label>
            {select("bond_stance")}
          </div>
          <div className="sk-field sk-full">
            <label className="sk-label" htmlFor="f-bond_comment">
              Anything you want to raise about it?
            </label>
            <p className="sk-help">
              Questions, concerns, or circumstances we should know about.
              Raising something here will not count against you — we would
              rather discuss it now than discover it in year two.
            </p>
            {area("bond_comment")}
          </div>
          <div className="sk-field sk-full">
            <label className="sk-label" htmlFor="f-bond_risk">
              Is there anything that could realistically pull you away within{" "}
              {BOND.yearsWord} years?<span className="sk-req">*</span>
            </label>
            <p className="sk-help">
              Family commitments overseas, a partner&rsquo;s career, a
              fellowship you intend to pursue elsewhere, or anything else.
              Honesty here protects both of us.
            </p>
            {area("bond_risk")}
          </div>
        </div>

        {tick(
          "bond_acknowledged",
          "I have read and understood the return-of-service commitment above, and I understand the binding terms will be provided in writing before I am asked to sign.",
        )}
      </Card>

      {/* Declaration. THREE consents, not one agreement: a statement of
          truthfulness, a permission the practice acts on when it contacts
          referees, and an acknowledgement that a live reporting session
          follows. The server refuses a submit unless all three and the
          bond acknowledgement are true. */}
      <div className="sk-declare" data-cy="app-declaration">
        <h2>Declaration</h2>
        <p>
          Your answers will be checked against your logbook, your references
          and a live reporting session.
        </p>
        {tick(
          "declare_true",
          "The information I have given is true and complete to the best of my knowledge.",
        )}
        {tick(
          "declare_verification",
          "I consent to verification of my qualifications, registration, logbook and references.",
        )}
        {tick(
          "declare_live_assessment",
          `I understand this application includes a live reporting assessment on ${practice}'s own systems.`,
        )}

        {/* Only when something WAS carried. A first application has
            nothing to re-affirm, and asking somebody to confirm answers
            they have just typed is noise that teaches them to tick
            without reading -- which would weaken the three above it. */}
        {carriedFrom &&
          tick(
            "carried_confirmed",
            `I have checked sections 01–06, carried over from ${carriedFrom}, and they are still correct.`,
          )}

        {!readOnly && (
        <>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 16 }}>
          <button
            type="button"
            className="sk-btn sk-btn-primary"
            data-cy="btn-submit-application"
            disabled={save.isPending}
            onClick={doSubmit}
          >
            {save.isPending
              ? "Saving…"
              : alreadySubmitted
                ? "Save changes and continue"
                : "Submit application"}
          </button>
        </div>
        <p className="sk-help" style={{ marginTop: 10 }}>
          Nothing is timed yet — the assessment clock does not start until you
          press &ldquo;Start the test&rdquo;. Your answers save as you go.
        </p>
        </>
        )}
      </div>

      {/* ADDRESSED TO THE APPLICANT, so it goes when somebody else is
          reading. It tells the person filling the form what their
          answers are used for and where to check their own registration
          — advice a reviewer can act on for nobody, and on the printed
          record it is a paragraph of standing text between them and the
          candidate's answers.

          THE CANDIDATE STILL RENDERS IT. This is a conditional, not a
          wrapper: at readOnly false the markup is the same element in
          the same place as before. */}
      {!readOnly && (
        <p className="sk-disclaimer">
          Your answers are used only to assess this application. Registration
          requirements are set by AHPRA / the Medical Board and RANZCR, and
          these pathways change — confirm your own status directly with them.
        </p>
      )}
      </ReadOnlyGate>
    </div>
  );
}
